import type { ErrorResponse } from "@apollo/client/link/error";
import type { Breadcrumb } from "@sentry/react";
import * as Sentry from "@sentry/react";
import { GraphQLError } from "graphql/error";
import type { Kind } from "graphql/language";
import { Config } from "services/configuration/eatSmartConfig";
import { logToSentry } from "../../utils/logger";

export const initSentry = () => {
	if (window.MSW_ENABLED) {
		return;
	}

	Sentry.init({
		// when undefined, sentry does not send any events
		dsn: Config.tools.sentryDSN,
		environment: import.meta.env.MODE,
		normalizeDepth: 6,
		release: `terminal-app@${import.meta.env.APP_VERSION}+${import.meta.env.VITE_BUILD_NUMBER}`,
		transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
		integrations: [
			Sentry.browserTracingIntegration(),
			Sentry.captureConsoleIntegration({
				levels: ["error", "warn"],
			}),
		],
		initialScope: {
			tags: {
				fridgeId: localStorage.getItem("fridgeId"),
				tabletType: localStorage.getItem("tabletType"),
			},
			contexts: {
				fridgeInfo: {
					fridgeId: localStorage.getItem("fridgeId"),
					tabletType: localStorage.getItem("tabletType"),
				},
			},
		},
		// Performance Monitoring
		tracesSampleRate: 1.0, //  Capture 100% of the transactions
		// Set 'tracePropagationTargets' to control for which
		// URLs distributed tracing should be enabled
		tracePropagationTargets: ["localhost"],
	});
};

type SentryErrorEnrichedData = {
	kind: string;
	query: Kind.DOCUMENT;
	variables: Record<string, string | number>;
};

// Collect data about fridge and GQL query for Sentry scope
const getErrorData = (error: ErrorResponse): SentryErrorEnrichedData => ({
	kind: error.operation.operationName,
	query: error.operation.query.kind,
	variables: error.operation.variables,
});

const getBreadcrumb = (graphQlError: GraphQLError): Breadcrumb => ({
	category: "query-path",
	message: graphQlError.path?.join(" > "),
});

export const captureApolloError = (
	error: Error,
	errorData: SentryErrorEnrichedData,
	extraData?: { [key: string]: unknown },
) => {
	Sentry.withScope((scope) => {
		if (errorData.kind) {
			scope.setTag("kind", errorData.kind);
		}
		if (errorData.query) {
			scope.setExtra("query", errorData.query);
		}
		if (errorData.variables) {
			scope.setExtra("variables", errorData.variables);
		}
		if (extraData) {
			for (const key of Object.keys(extraData)) {
				scope.setExtra(key, extraData[key]);
			}
		}
		if (error instanceof GraphQLError && error?.path) {
			scope.addBreadcrumb(getBreadcrumb(error));
		}
		Sentry.captureException(error);
	});
};

export const handleSendApolloErrorToSentry = (error: ErrorResponse) => {
	const errorData = getErrorData(error);

	if (error.graphQLErrors) {
		for (const graphQlError of error.graphQLErrors) {
			captureApolloError({ ...graphQlError, name: graphQlError.message }, errorData);
		}
		return;
	}

	if (error.networkError) {
		captureApolloError(error.networkError, errorData);
		return;
	}
	logToSentry("Application error", "error", [JSON.stringify(error)]);
};
